import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { BookOpen, ArrowRight, FileText, BookOpenCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextbooksCard } from "@/components/TextbooksCard";

export default function ChapterList() {
  const { classId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/classes/${classId}/chapters`).then((r) => setData(r.data));
  }, [classId]);

  if (!data) return <div className="text-zinc-500">Loading…</div>;

  return (
    <div>
      <div className="mb-6 fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">{data.class_label}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Chapters</h1>
          <p className="text-zinc-400 mt-2 text-sm">
            NCERT-aligned. Tap any chapter for theory, worked solutions and extra practice.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/worksheets/${classId}`}>
            <Button
              className="bg-gradient-to-r from-pink-300 to-orange-300 hover:from-pink-200 hover:to-orange-200 text-slate-900 rounded-full font-semibold"
              data-testid="cta-advanced-worksheets"
            >
              <BookOpenCheck className="w-4 h-4 mr-1" /> Advanced worksheets
            </Button>
          </Link>
          <Link to={`/sample-papers/${classId}`}>
            <Button className="bg-sky-400 hover:bg-sky-300 text-slate-900 rounded-full font-semibold" data-testid="cta-sample-papers">
              <FileText className="w-4 h-4 mr-1" /> Sample papers
            </Button>
          </Link>
        </div>
      </div>

      <TextbooksCard classId={classId} />

      {/* Advanced practice worksheets banner */}
      <Link
        to={`/worksheets/${classId}`}
        className="fade-up mb-6 flex items-center gap-4 rounded-lg border border-pink-300/25 bg-gradient-to-r from-pink-300/[0.05] via-orange-300/[0.05] to-yellow-300/[0.05] p-4 hover:border-pink-300/50 transition-colors"
        data-testid="worksheets-banner"
      >
        <div className="w-10 h-10 rounded-full bg-pink-300/10 grid place-items-center shrink-0">
          <Sparkles className="w-5 h-5 text-pink-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 mb-0.5">New · free for now</div>
          <div className="text-sm text-zinc-200">
            Advanced Practice Worksheets for {data.class_label} — printable, exam-level packs
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-pink-300 shrink-0" />
      </Link>

      <div className="grid md:grid-cols-2 gap-3" data-testid="chapter-list">
        {data.chapters.map((c, i) => (
          <Link
            key={c.id}
            to={`/chapters/${c.id}`}
            className={`fade-up fade-up-${(i % 4) + 1} lift rounded-lg border border-white/10 bg-[#152040] p-5 flex gap-4 items-start`}
            data-testid={`chapter-item-${c.id}`}
          >
            <div className="font-mono text-yellow-300 text-xl w-10 flex-shrink-0">{String(c.number).padStart(2, "0")}</div>
            <div className="flex-1">
              <div className="font-display text-lg font-semibold mb-1">{c.title}</div>
              <div className="text-sm text-zinc-400 leading-relaxed mb-3">{c.summary}</div>
              <div className="flex gap-3 text-[11px] uppercase tracking-wider text-zinc-500">
                <span>{c.exercise_count} solved</span>
                <span>{c.extra_count} extra</span>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-zinc-500 mt-1" />
          </Link>
        ))}
      </div>
    </div>
  );
}
